import type { TimelineStateUpdaterOps } from '../public/timelineStateUpdaterOps';
import type { ModelVideoTrackBox, ModelAudioTrackBox, ModelTrackBox } from './timelineModel';
import type { SourceRef } from './sourceRef';
import type { Fraction } from '@mimir/common/util/fractionType';

/**
 * This helper class is used to apply low level updates to all consumers,
 * i.e. to update the internal state, the UI state and the rendering state.
 */
export class CompositeTimelineStateUpdater implements TimelineStateUpdaterOps {

  constructor(private readonly updaters: TimelineStateUpdaterOps[]) {}

  addVideoTrack(): void {
    this.updaters.forEach(u => u.addVideoTrack());
  }

  addAudioTrack(): void {
    this.updaters.forEach(u => u.addAudioTrack());
  }

  addSource(sourceRef: SourceRef): void {
    this.updaters.forEach(u => u.addSource(sourceRef));
  }

  removeSource(sourceRef: SourceRef): void {
    this.updaters.forEach(u => u.removeSource(sourceRef));
  }

  updateUndoRedoStatus(canUndo: boolean, canRedo: boolean): void {
    this.updaters.forEach(u => u.updateUndoRedoStatus(canUndo, canRedo));
  }

  insertVideoTrackBox(trackIndex: number, index: number, box: ModelVideoTrackBox): void {
    this.updaters.forEach(u => u.insertVideoTrackBox(trackIndex, index, box));
  }

  removeVideoTrackBox(trackIndex: number, box: ModelVideoTrackBox): void {
    this.updaters.forEach(u => u.removeVideoTrackBox(trackIndex, box));
  }

  addAudioTrackBox(trackIndex: number, box: ModelAudioTrackBox): void {
    this.updaters.forEach(u => u.addAudioTrackBox(trackIndex, box));
  }

  removeAudioTrackBox(trackIndex: number, box: ModelAudioTrackBox): void {
    this.updaters.forEach(u => u.removeAudioTrackBox(trackIndex, box));
  }

  setOutPoint(box: ModelTrackBox, outPoint: Fraction): void {
    this.updaters.forEach(u => u.setOutPoint(box, outPoint));
  }

  setInPoint(box: ModelTrackBox, inPoint: Fraction): void {
    this.updaters.forEach(u => u.setInPoint(box, inPoint));
  }

  setSourceStart(sourceRef: SourceRef, start: Fraction): void {
    this.updaters.forEach(u => u.setSourceStart(sourceRef, start));
  }

  setSourceDragged(sourceRef: SourceRef, dragged: boolean): void {
    this.updaters.forEach(u => u.setSourceDragged(sourceRef, dragged));
  }

  setBoxStart(box: ModelTrackBox, start: Fraction): void {
    this.updaters.forEach(u => u.setBoxStart(box, start));
  }

  sortTracks(): void {
    this.updaters.forEach(u => u.sortTracks());
  }
}
